import { INCENTIVE_SCENARIOS } from 'utils/subscriptionPlanUtils';

/**
 * Upgrade Incentive Helper
 * Service to manage upgrade incentive modal timing and tracking
 * Handles when and how often to show upgrade prompts to users
 */

const STORAGE_KEY = 'violerts_upgrade_incentive_tracking';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const THREE_DAY_MS = 3 * ONE_DAY_MS;
// const SEVEN_DAY_MS = 7 * ONE_DAY_MS;

/**
 * Get tracking data for a specific user
 * @param {string} userId - User ID
 * @returns {Object} User tracking data
 */
const getUserTracking = (userId) => {
	if (!userId) return {};

	try {
		const allTracking = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
		return allTracking[userId] || {};
	} catch (error) {
		console.error('Error reading upgrade incentive tracking:', error);
		return {};
	}
};

/**
 * Save tracking data for a specific user
 * @param {string} userId - User ID
 * @param {Object} data - Tracking data to save
 */
const saveUserTracking = (userId, data) => {
	if (!userId) return;

	try {
		const allTracking = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
		allTracking[userId] = { ...allTracking[userId], ...data };
		localStorage.setItem(STORAGE_KEY, JSON.stringify(allTracking));
	} catch (error) {
		console.error('Error saving upgrade incentive tracking:', error);
	}
};

/**
 * Check if we should show the upgrade incentive modal
 * @param {string} scenario - Type of incentive scenario
 * @param {string} userId - User ID
 * @param {Object} options - Additional options
 * @returns {boolean} Whether to show the modal
 */
export function shouldShowUpgradeIncentive(scenario, userId, options = {}) {
	if (options?.force) return true;
	// Without a userId we cannot read or write throttling state, so suppress
	// the modal rather than showing it on every trigger.
	if (!userId) return false;

	const userTracking = getUserTracking(userId);
	const now = Date.now();


	switch (scenario) {
		case INCENTIVE_SCENARIOS.FREE_USER_FEATURE:
			// Show once per session for feature blocking
			if (userTracking.featureBlockShownThisSession) {
				return false;
			}

			// Don't show if recently dismissed (1-day cooldown for feature blocks)
			if (
				userTracking.lastFeatureDismissed &&
				now - userTracking.lastFeatureDismissed < ONE_DAY_MS
			) {
				return false;
			}

			// Don't show more than 5 times total for feature blocks
			if ((userTracking.featureBlockCount || 0) >= 5) {
				return false;
			}

			return true;

		case INCENTIVE_SCENARIOS.FREE_USER_DAILY:
			// Show once per day for dashboard nudges
			if (
				userTracking.lastDailyShown &&
				now - userTracking.lastDailyShown < ONE_DAY_MS
			) {
				return false;
			}

			// Don't show if recently dismissed (3-day cooldown for daily nudges)
			if (
				userTracking.lastDailyDismissed &&
				now - userTracking.lastDailyDismissed < THREE_DAY_MS
			) {
				return false;
			}

			// Don't show more than 3 times total for daily nudges
			if ((userTracking.dailyNudgeCount || 0) >= 3) {
				return false;
			}

			return true;

		case INCENTIVE_SCENARIOS.CANCEL_USER:
			// Always show for cancellation (critical retention moment)
			return true;

		default:
			// For any other scenarios, always show (can be customized later)
			return true;
	}
}

/**
 * Track that the upgrade incentive modal was shown
 * @param {string} scenario - Type of incentive scenario
 * @param {string} userId - User ID
 */
export function trackUpgradeIncentiveShown(scenario, userId) {
	if (!userId) return;

	const now = Date.now();
	const updates = {
		lastShown: now,
		totalShowCount: (getUserTracking(userId).totalShowCount || 0) + 1,
	};

	// eslint-disable-next-line default-case
	switch (scenario) {
		case INCENTIVE_SCENARIOS.FREE_USER_FEATURE:
			updates.featureBlockShownThisSession = true;
			updates.featureBlockCount =
				(getUserTracking(userId).featureBlockCount || 0) + 1;
			updates.lastFeatureShown = now;
			break;

		case INCENTIVE_SCENARIOS.FREE_USER_DAILY:
			updates.lastDailyShown = now;
			updates.dailyNudgeCount =
				(getUserTracking(userId).dailyNudgeCount || 0) + 1;
			break;

		case INCENTIVE_SCENARIOS.CANCEL_USER:
			updates.cancelIncentiveShown = now;
			break;
	}

	saveUserTracking(userId, updates);
}

/**
 * Track that the upgrade incentive modal was dismissed
 * @param {string} scenario - Type of incentive scenario
 * @param {string} userId - User ID
 */
export function trackUpgradeIncentiveDismissed(scenario, userId) {
	if (!userId) return;

	const now = Date.now();
	const updates = {
		lastDismissed: now,
		totalDismissalCount: (getUserTracking(userId).totalDismissalCount || 0) + 1,
	};

	// eslint-disable-next-line default-case
	switch (scenario) {
		case INCENTIVE_SCENARIOS.FREE_USER_FEATURE:
			updates.lastFeatureDismissed = now;
			updates.featureBlockShownThisSession = false; // Reset session flag
			break;

		case INCENTIVE_SCENARIOS.FREE_USER_DAILY:
			updates.lastDailyDismissed = now;
			break;
	}

	saveUserTracking(userId, updates);
}

/**
 * Track that user clicked upgrade/converted
 * @param {string} scenario - Type of incentive scenario
 * @param {string} userId - User ID
 * @param {string} planId - Selected plan ID
 */
export function trackUpgradeIncentiveConverted(scenario, userId, planId) {
	if (!userId) return;

	const updates = {
		lastConverted: Date.now(),
		convertedFromScenario: scenario,
		convertedToPlan: planId,
		totalConversions: (getUserTracking(userId).totalConversions || 0) + 1,
	};

	saveUserTracking(userId, updates);
}

/**
 * Reset session-specific tracking (call on app load/session start)
 * @param {string} userId - User ID
 */
export function resetSessionTracking(userId) {
	if (!userId) return;

	const updates = {
		featureBlockShownThisSession: false,
	};

	saveUserTracking(userId, updates);
}

/**
 * Get analytics data for upgrade incentive performance
 * @param {string} userId - User ID
 * @returns {Object} Analytics data
 */
export function getUpgradeIncentiveAnalytics(userId) {
	if (!userId) return null;

	const tracking = getUserTracking(userId);

	return {
		totalShown: tracking.totalShowCount || 0,
		totalDismissed: tracking.totalDismissalCount || 0,
		totalConverted: tracking.totalConversions || 0,
		conversionRate: tracking.totalShowCount
			? (
					((tracking.totalConversions || 0) / tracking.totalShowCount) *
					100
			  ).toFixed(1) + '%'
			: '0%',
		lastShown: tracking.lastShown ? new Date(tracking.lastShown) : null,
		lastConverted: tracking.lastConverted
			? new Date(tracking.lastConverted)
			: null,
		scenarios: {
			featureBlocks: tracking.featureBlockCount || 0,
			dailyNudges: tracking.dailyNudgeCount || 0,
			cancelIncentiveShown: !!tracking.cancelIncentiveShown,
		},
	};
}

/**
 * Clean up old tracking data (call periodically)
 * Removes data older than 90 days to prevent localStorage bloat
 */
export function cleanupOldTrackingData() {
	try {
		const allTracking = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
		const now = Date.now();
		const ninetyDaysMs = 90 * ONE_DAY_MS;

		let cleaned = false;
		Object.keys(allTracking).forEach((userId) => {
			const userTracking = allTracking[userId];
			if (
				userTracking.lastShown &&
				now - userTracking.lastShown > ninetyDaysMs
			) {
				delete allTracking[userId];
				cleaned = true;
			}
		});

		if (cleaned) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(allTracking));
		}
	} catch (error) {
		console.error('Error cleaning up upgrade incentive tracking:', error);
	}
}

/**
 * Helper to show upgrade incentive modal with proper tracking
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} openModal - openModal action creator
 * @param {Object} MODAL_TYPES - Modal types object
 * @param {string} scenario - Scenario type
 * @param {string} userId - User ID
 * @param {Object} options - Additional options
 * @returns {boolean} Whether modal was shown
 */
export function showUpgradeIncentiveIfAllowed(
	dispatch,
	openModal,
	MODAL_TYPES,
	scenario,
	userId,
	options = {}
) {
	if (!shouldShowUpgradeIncentive(scenario, userId, options)) {
		return false;
	}

	// Track that we're showing the modal
	trackUpgradeIncentiveShown(scenario, userId);

	if (options?.onConvert) {
		options = {
			...options,
			onConvert: (planId) => {
				trackUpgradeIncentiveConverted(scenario, userId, planId);
				if (options.onConvert) {
					options.onConvert(planId);
				}
			},
		};
	}

	// Show the modal
	dispatch(
		openModal(MODAL_TYPES.UPGRADE_INCENTIVE, {
			scenario,
			...options,
			onDismiss: () => {
				trackUpgradeIncentiveDismissed(scenario, userId);
				console.log('Upgrade incentive dismissed:', options);
				if (options.onDismiss) {
					options.onDismiss();
				}
			},

			// Pass through cancellation callback for cancel_user scenario
			onProceedWithCancel: options.onProceedWithCancel,
		})
	);

	return true;
}
